import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Link,
    TextField,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';

const emptyForm = {
    version: '',
    title: '',
    changelog: '',
    download_windows: '',
    download_mac: '',
    download_linux: '',
    active: true,
};

function DesktopReleaseFormPage({ mode }) {
    const isEdit = mode === 'edit';
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [checking, setChecking] = useState(!isMasterUser());
    const [formData, setFormData] = useState(emptyForm);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [loadingRelease, setLoadingRelease] = useState(isEdit);

    useEffect(() => {
        let cancelled = false;
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    if (!cancelled) {
                        setIsMaster(user.is_master);
                    }
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setIsMaster(false);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setChecking(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!isEdit || !isMaster || !id) {
            return undefined;
        }
        let cancelled = false;
        setLoadingRelease(true);
        api.get(`/desktop-releases/${id}`)
            .then((response) => {
                const release = response?.data?.data;
                if (!cancelled && release) {
                    setFormData({
                        version: release.version || '',
                        title: release.title || '',
                        changelog: release.changelog || '',
                        download_windows: release.download_windows || '',
                        download_mac: release.download_mac || '',
                        download_linux: release.download_linux || '',
                        active: release.active !== false,
                    });
                }
            })
            .catch(async () => {
                await Swal.fire({ icon: 'error', title: 'Versão não encontrada' });
                navigate('/admin/desktop-releases');
            })
            .finally(() => {
                if (!cancelled) {
                    setLoadingRelease(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [isEdit, isMaster, id, navigate]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setErrors({});
        try {
            const payload = {
                version: formData.version.trim(),
                title: formData.title.trim() || `Versão ${formData.version.trim()}`,
                changelog: formData.changelog.trim(),
                download_windows: formData.download_windows.trim() || undefined,
                download_mac: formData.download_mac.trim() || undefined,
                download_linux: formData.download_linux.trim() || undefined,
                active: formData.active !== false,
            };

            if (isEdit) {
                await api.put(`/desktop-releases/${id}`, payload);
            } else {
                await api.post('/desktop-releases', payload);
            }

            await Swal.fire({
                icon: 'success',
                title: isEdit ? 'Versão atualizada' : 'Versão publicada',
                timer: 1600,
                showConfirmButton: false,
            });
            navigate('/admin/desktop-releases');
        } catch (error) {
            const apiErrors = error?.response?.data?.errors;
            if (apiErrors) {
                setErrors(apiErrors);
            } else {
                await Swal.fire({
                    icon: 'error',
                    title: 'Falha ao salvar',
                    text: error?.response?.data?.message || error?.response?.data?.error || 'Tente novamente.',
                });
            }
        } finally {
            setLoading(false);
        }
    };

    if (checking || loadingRelease) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }
    if (!isMaster) {
        return <Navigate to="/admin" replace />;
    }

    return (
        <Box>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Dashboard
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/desktop-releases">
                    Versões Desktop
                </Link>
                <Typography color="text.primary">{isEdit ? 'Editar' : 'Nova'}</Typography>
            </Breadcrumbs>

            <Typography variant="h5" gutterBottom>
                {isEdit ? 'Editar versão' : 'Publicar versão'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A release mais recente (por data de publicação) aparece na tela de Integrações.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 720 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    Deixe o link vazio para ocultar o botão daquele sistema operacional.
                </Alert>

                <TextField
                    label="Versão"
                    placeholder="1.2.0"
                    fullWidth
                    required
                    margin="normal"
                    value={formData.version}
                    onChange={(e) => handleChange('version', e.target.value)}
                    error={Boolean(errors.version)}
                    helperText={errors.version}
                />
                <TextField
                    label="Título"
                    placeholder="Melhorias de sincronização"
                    fullWidth
                    margin="normal"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    error={Boolean(errors.title)}
                    helperText={errors.title || 'Se vazio, usa “Versão X”.'}
                />
                <TextField
                    label="Novidades"
                    fullWidth
                    required
                    multiline
                    minRows={5}
                    margin="normal"
                    value={formData.changelog}
                    onChange={(e) => handleChange('changelog', e.target.value)}
                    error={Boolean(errors.changelog)}
                    helperText={errors.changelog}
                />
                <TextField
                    label="Link Windows"
                    fullWidth
                    margin="normal"
                    value={formData.download_windows}
                    onChange={(e) => handleChange('download_windows', e.target.value)}
                    error={Boolean(errors.download_windows)}
                    helperText={errors.download_windows}
                />
                <TextField
                    label="Link Mac"
                    fullWidth
                    margin="normal"
                    value={formData.download_mac}
                    onChange={(e) => handleChange('download_mac', e.target.value)}
                    error={Boolean(errors.download_mac)}
                    helperText={errors.download_mac}
                />
                <TextField
                    label="Link Linux"
                    fullWidth
                    margin="normal"
                    value={formData.download_linux}
                    onChange={(e) => handleChange('download_linux', e.target.value)}
                    error={Boolean(errors.download_linux)}
                    helperText={errors.download_linux}
                />
                <FormControlLabel
                    sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}
                    control={
                        <Checkbox
                            checked={formData.active !== false}
                            onChange={(e) => handleChange('active', e.target.checked)}
                            color="primary"
                        />
                    }
                    label="Versão ativa (links de download visíveis na tela de Integrações e no histórico público)"
                />

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button type="submit" variant="contained" disabled={loading}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : 'Salvar'}
                    </Button>
                    <Button variant="outlined" onClick={() => navigate('/admin/desktop-releases')} disabled={loading}>
                        Cancelar
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}

export function NewDesktopReleasePage() {
    return <DesktopReleaseFormPage mode="create" />;
}

export function EditDesktopReleasePage() {
    return <DesktopReleaseFormPage mode="edit" />;
}

export default NewDesktopReleasePage;
